import KpiChart from "@app/components/kpi-chart";
import { useState } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { churnPage_Query } from "@app/queries/churnPage_Query.graphql";

export const churnQuery = graphql`
  query churnPage_Query($from: String, $to: String) {
    churn(from: $from, to: $to) {
      cohorts {
        cohort
        size
        logoRetention
        revenueRetention
      }
      grossRevenueChurn {
        month
        value
      }
      netRevenueChurn {
        month
        value
      }
      logoChurn {
        month
        value
      }
    }
    churnBreakdown(from: $from, to: $to) {
      tenure {
        ...churnPage_bucket @relay(mask: false)
      }
      accountSize {
        ...churnPage_bucket @relay(mask: false)
      }
      plan {
        ...churnPage_bucket @relay(mask: false)
      }
    }
  }
`;

graphql`
  fragment churnPage_bucket on ChurnBucket @relay(mask: false) {
    bucket
    churnedCustomers
    churnedMrr
    activeCustomerMonths
    avgMonthlyLogoChurn
    monthlyChurnedMrr {
      month
      value
    }
  }
`;

type Bucket = churnPage_Query["response"]["churnBreakdown"]["tenure"][number];

const PALETTE = [
  "#0f766e",
  "#2563eb",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#475569",
];
const MAX_CHART_BUCKETS = 6;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

// Green at 100%+ retention fading to red at 0%.
function retentionColor(value: number): string {
  const clamped = Math.max(0, Math.min(value, 1.2)) / 1.2;
  const hue = clamped * 120;
  return `hsl(${hue}, 65%, ${85 - clamped * 20}%)`;
}

function BreakdownSection({
  title,
  subtitle,
  buckets,
}: {
  title: string;
  subtitle: string;
  buckets: ReadonlyArray<Bucket>;
}) {
  if (buckets.length === 0) return null;

  // One shared month axis so the stacked bars line up.
  const months = [
    ...new Set(buckets.flatMap((b) => b.monthlyChurnedMrr.map((p) => p.month))),
  ].sort();
  // Keep the backend's bucket order (tenure shortest→longest, seats smallest→
  // largest, plan by lost MRR) rather than re-sorting, so the legend/colors are
  // ordered meaningfully.
  const charted = buckets.slice(0, MAX_CHART_BUCKETS);
  const series = charted.map((bucket, i) => {
    const byMonth = new Map(
      bucket.monthlyChurnedMrr.map((p) => [p.month, p.value])
    );
    return {
      name: bucket.bucket,
      type: "bar" as const,
      stack: "churn",
      color: PALETTE[i % PALETTE.length],
      points: months.map((month) => ({
        month,
        value: byMonth.get(month) ?? 0,
      })),
    };
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-bold">{title}</h2>
        <p className="text-xs text-gray-400">{subtitle}</p>
      </div>

      {months.length > 0 && (
        <KpiChart
          title="Churned MRR by month"
          series={series}
          format="money"
          onMonthClick={() => {}}
        />
      )}

      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 uppercase border-b">
              <th className="px-3 py-2">Bucket</th>
              <th className="px-3 py-2 text-right">Churned customers</th>
              <th className="px-3 py-2 text-right">Churned MRR</th>
              <th className="px-3 py-2 text-right">Active customer-months</th>
              <th className="px-3 py-2 text-right">Avg monthly churn</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((bucket) => (
              <tr key={bucket.bucket} className="border-b last:border-b-0">
                <td className="px-3 py-2 font-medium">{bucket.bucket}</td>
                <td className="px-3 py-2 text-right">
                  {bucket.churnedCustomers}
                </td>
                <td className="px-3 py-2 text-right">
                  {money(bucket.churnedMrr)}
                </td>
                <td className="px-3 py-2 text-right">
                  {bucket.activeCustomerMonths}
                </td>
                <td className="px-3 py-2 text-right">
                  {(bucket.avgMonthlyLogoChurn * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function ChurnPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<churnPage_Query>;
}) {
  const data = usePreloadedQuery(churnQuery, queryRef);
  const [mode, setMode] = useState<"revenue" | "logo">("revenue");

  const cohorts = data.churn.cohorts;
  const { tenure, accountSize, plan } = data.churnBreakdown;
  const hasData =
    cohorts.length > 0 ||
    tenure.length > 0 ||
    accountSize.length > 0 ||
    plan.length > 0;

  if (!hasData) {
    return (
      <div className="p-6 text-gray-500 space-y-2">
        <h1 className="text-xl font-bold text-gray-900">Churn & retention</h1>
        <p>
          No Stripe data synced yet. Set{" "}
          <code className="font-mono">STRIPE_API_KEY</code> in{" "}
          <code className="font-mono">.env</code> and run{" "}
          <code className="font-mono">rake stripe:sync</code>, or load CSV
          exports with <code className="font-mono">rake stripe:load_csvs</code>.
        </p>
      </div>
    );
  }

  const maxOffsets = Math.max(0, ...cohorts.map((c) => c.logoRetention.length));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Churn & retention</h1>
        <div className="flex rounded border overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setMode("revenue")}
            className={`px-3 py-1.5 ${
              mode === "revenue"
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-600"
            }`}
          >
            Revenue retention
          </button>
          <button
            type="button"
            onClick={() => setMode("logo")}
            className={`px-3 py-1.5 ${
              mode === "logo"
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-600"
            }`}
          >
            Logo retention
          </button>
        </div>
      </div>

      {cohorts.length > 0 && (
        <div className="overflow-x-auto bg-white rounded border p-4">
          <table
            className="text-xs border-separate"
            style={{ borderSpacing: 2 }}
          >
            <thead>
              <tr className="text-gray-400">
                <th className="text-left pr-2 sticky left-0 bg-white">
                  Cohort
                </th>
                <th className="pr-2">Size</th>
                {Array.from({ length: maxOffsets }, (_, i) => (
                  <th key={i} className="font-normal w-10">
                    M{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort) => {
                const retention =
                  mode === "revenue"
                    ? cohort.revenueRetention
                    : cohort.logoRetention;

                return (
                  <tr key={cohort.cohort}>
                    <td className="pr-2 font-medium sticky left-0 bg-white whitespace-nowrap">
                      {cohort.cohort}
                    </td>
                    <td className="pr-2 text-gray-400 text-center">
                      {cohort.size}
                    </td>
                    {retention.map((value, i) => (
                      <td
                        key={i}
                        className="text-center rounded-sm tabular-nums"
                        style={{
                          backgroundColor: retentionColor(value),
                          minWidth: 38,
                        }}
                        title={`${cohort.cohort} +${i}mo: ${(
                          value * 100
                        ).toFixed(0)}% ${mode} retention`}
                      >
                        {(value * 100).toFixed(0)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] text-gray-400">
            Cohort size = paying logos: distinct customers in their first month
            of positive subscription MRR. This is intentionally lower than
            Stripe's billing dashboard (~7%), which counts every subscription
            that becomes active — including trials, $0/free plans, and
            unpaid-but-active — on a contracted-MRR basis. We use paying logos
            because it's the correct denominator for revenue and logo retention.
            An exact tie-out to Stripe's number requires its internal
            customer_change_events log (Sigma only), so the two are reconciled
            by definition, not matched.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KpiChart
          title="Revenue churn (monthly)"
          series={[
            {
              name: "Gross churn",
              points: data.churn.grossRevenueChurn,
              type: "line",
              color: "#dc2626",
            },
            {
              name: "Net churn",
              points: data.churn.netRevenueChurn,
              type: "line",
              color: "#2563eb",
            },
          ]}
          format="percent"
          onMonthClick={() => {}}
        />
        <KpiChart
          title="Logo churn (monthly)"
          series={[
            {
              name: "Logo churn",
              points: data.churn.logoChurn,
              type: "line",
              color: "#d97706",
            },
          ]}
          format="percent"
          onMonthClick={() => {}}
        />
      </div>

      <div className="border-t pt-6 space-y-8">
        <div>
          <h2 className="text-lg font-bold">Churn breakdown</h2>
          <p className="text-xs text-gray-400">
            A churn event = a customer with paid revenue one month and none the
            next; lost MRR is their last active month's revenue. Avg monthly
            churn = events / active customer-months in the bucket.
          </p>
        </div>

        <BreakdownSection
          title="By tenure"
          subtitle="Months on the system at the time of churn, from invoice history"
          buckets={tenure}
        />
        <BreakdownSection
          title="By account size (monthly spend)"
          subtitle="Subscription MRR band in the customer's last active month — always known for a paying account, unlike seats"
          buckets={accountSize}
        />
        <BreakdownSection
          title="By plan"
          subtitle="Plan from the invoice line in the last active month (point-in-time); top plans by churned MRR plus Other"
          buckets={plan}
        />
      </div>

      <p className="text-xs text-gray-400">
        Net churn below zero means expansion outweighs losses. A customer counts
        as active in a month when a paid invoice exists (monthly billing
        assumption; annual plans are not spread). The in-progress month is
        excluded from churn.
      </p>
    </div>
  );
}
